import moment from "moment";
import { Fragment, useCallback, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import submit from "submit-form";
import ConfirmationModal from "../../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../../parts/SvgIcon";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import { sanitizeTipTapDocJson } from "./noteTipTapJson";

/** Enough characters that two lines typically fill before CSS line-clamp trims. */
const PREVIEW_TRUNCATE = 240;
const SNIPPET_BEFORE = 52;
const SNIPPET_AFTER = 140;

const extractPlainTextFromDoc = (rawContent) => {
  if (!rawContent) return "";

  let content = rawContent;
  if (typeof content === "string") {
    try {
      content = JSON.parse(content);
    } catch {
      return content.trim();
    }
  }

  if (!content.content) return "";

  const extractText = (node, listCtx = null) => {
    if (node.text) return node.text;

    if (node.type === "bulletList" && node.content) {
      return node.content.map((item) => extractText(item, "bullet")).join(" ");
    }

    if (node.type === "orderedList" && node.content) {
      return node.content
        .map((item, i) => extractText(item, { kind: "ordered", n: i + 1 }))
        .join(" ");
    }

    if (node.type === "listItem" && node.content) {
      const inner = node.content
        .map((c) => extractText(c, null))
        .join(" ")
        .trim();
      if (listCtx === "bullet") return inner ? `• ${inner}` : "";
      if (listCtx?.kind === "ordered")
        return inner ? `${listCtx.n}. ${inner}` : "";
      return inner;
    }

    if (node.content)
      return node.content.map((c) => extractText(c, null)).join(" ");

    return "";
  };

  return content.content
    .map((n) => extractText(n, null))
    .join(" ")
    .trim();
};

/** Pasted or legacy content sometimes includes literal tags in plain text snippets. */
const stripHtmlTagsForPreview = (str) => str.replace(/<[^>]+>/g, "");

const parseDocForPreview = (rawContent) => {
  if (!rawContent) return null;

  let content = rawContent;
  if (typeof content === "string") {
    try {
      content = JSON.parse(content);
    } catch {
      const t = content.trim();
      if (!t) return null;
      return sanitizeTipTapDocJson({
        type: "doc",
        content: [{ type: "paragraph", content: [{ type: "text", text: t }] }],
      });
    }
  }

  if (!content?.content?.length) return null;
  return sanitizeTipTapDocJson(content);
};

const wrapMarkedText = (text, marks, reactKey) => {
  let inner = text;
  const list = marks || [];
  for (let i = list.length - 1; i >= 0; i--) {
    const m = list[i];
    switch (m.type) {
      case "bold":
        inner = <strong>{inner}</strong>;
        break;
      case "italic":
        inner = <em>{inner}</em>;
        break;
      case "underline":
        inner = <u>{inner}</u>;
        break;
      case "strike":
        inner = <s>{inner}</s>;
        break;
      case "code":
        inner = <code>{inner}</code>;
        break;
      case "link":
        inner = (
          <span
            className="note-card-preview-link"
            title={m.attrs?.href ? String(m.attrs.href) : undefined}
          >
            {inner}
          </span>
        );
        break;
      default:
        break;
    }
  }
  return <Fragment key={reactKey}>{inner}</Fragment>;
};

const buildRichPreviewNodes = (doc, maxChars) => {
  const out = [];
  let used = 0;
  let done = false;
  let ellipsisShown = false;
  let key = 0;

  const appendEllipsis = () => {
    if (ellipsisShown) return;
    ellipsisShown = true;
    out.push(<span key={`e-${key++}`}>...</span>);
  };

  const addSep = () => {
    if (out.length > 0 && !done) {
      out.push(<span key={`s-${key++}`}> </span>);
    }
  };

  const takeText = (text, marks) => {
    if (done || !text) return;
    const rest = maxChars - used;
    if (rest <= 0) {
      done = true;
      return;
    }
    if (text.length <= rest) {
      used += text.length;
      out.push(wrapMarkedText(text, marks, `n-${key++}`));
    } else {
      const slice = text.slice(0, rest);
      used += rest;
      out.push(wrapMarkedText(slice, marks, `n-${key++}`));
      done = true;
      appendEllipsis();
    }
  };

  const walkInline = (nodes) => {
    if (!nodes || done) return;
    for (const n of nodes) {
      if (done) break;
      if (n.type === "text") takeText(n.text, n.marks);
      else if (n.type === "hardBreak") takeText(" ", null);
    }
  };

  const walkBlock = (node) => {
    if (done || !node) return;
    switch (node.type) {
      case "paragraph":
        addSep();
        walkInline(node.content);
        break;
      case "heading":
        addSep();
        walkInline(node.content);
        break;
      case "blockquote":
        for (const c of node.content || []) walkBlock(c);
        break;
      case "bulletList":
        for (const li of node.content || []) {
          addSep();
          out.push(<span key={`b-${key++}`}>• </span>);
          walkBlock(li);
        }
        break;
      case "orderedList": {
        let idx = 0;
        for (const li of node.content || []) {
          addSep();
          out.push(<span key={`o-${key++}`}>{`${idx + 1}. `}</span>);
          walkBlock(li);
          idx += 1;
        }
        break;
      }
      case "listItem":
        for (const c of node.content || []) walkBlock(c);
        break;
      case "taskList":
        for (const c of node.content || []) walkBlock(c);
        break;
      case "taskItem": {
        addSep();
        const checked = Boolean(node.attrs?.checked);
        out.push(<span key={`tk-${key++}`}>{checked ? "☑ " : "☐ "}</span>);
        for (const c of node.content || []) walkBlock(c);
        break;
      }
      case "codeBlock": {
        addSep();
        const codeText = (node.content || [])
          .map((t) => (t.type === "text" ? t.text : ""))
          .join("\n");
        if (!codeText) break;
        const rest = maxChars - used;
        if (rest <= 0) {
          done = true;
          break;
        }
        if (codeText.length <= rest) {
          used += codeText.length;
          out.push(
            <code key={`cb-${key++}`} className="note-card-preview-code">
              {codeText}
            </code>
          );
        } else {
          used += rest;
          out.push(
            <code key={`cb-${key++}`} className="note-card-preview-code">
              {codeText.slice(0, rest)}
            </code>
          );
          done = true;
          appendEllipsis();
        }
        break;
      }
      case "horizontalRule":
        addSep();
        takeText("—", null);
        break;
      default:
        if (Array.isArray(node.content)) {
          for (const c of node.content) walkBlock(c);
        }
    }
  };

  for (const c of doc.content || []) walkBlock(c);
  return out.length ? out : null;
};

const indexOfInsensitive = (haystack, needle) => {
  if (!needle) return -1;
  return haystack.toLowerCase().indexOf(needle.toLowerCase());
};

const buildSnippetAroundMatch = (fullText, needle) => {
  const idx = indexOfInsensitive(fullText, needle);
  if (idx === -1) return null;

  const start = Math.max(0, idx - SNIPPET_BEFORE);
  const end = Math.min(fullText.length, idx + needle.length + SNIPPET_AFTER);
  const lead = start > 0 ? "…" : "";
  const trail = end < fullText.length ? "…" : "";
  return `${lead}${fullText.slice(start, end)}${trail}`;
};

const splitForHighlight = (text, needle) => {
  if (!needle || !text) return [{ key: "all", text, highlight: false }];

  const lowerText = text.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  const n = lowerNeedle.length;
  const parts = [];
  let i = 0;
  let seq = 0;

  while (i < text.length) {
    const j = lowerText.indexOf(lowerNeedle, i);
    if (j === -1) {
      parts.push({
        key: `t-${seq++}`,
        text: text.slice(i),
        highlight: false,
      });
      break;
    }
    if (j > i) {
      parts.push({
        key: `t-${seq++}`,
        text: text.slice(i, j),
        highlight: false,
      });
    }
    parts.push({
      key: `h-${j}-${n}`,
      text: text.slice(j, j + n),
      highlight: true,
    });
    i = j + n;
  }

  return parts;
};

const HighlightedText = ({
  text,
  searchQuery,
  as: Component = "span",
  ...rest
}) => {
  const q = searchQuery?.trim();
  if (!q || !text) {
    return <Component {...rest}>{text}</Component>;
  }

  const parts = splitForHighlight(text, q);
  return (
    <Component {...rest}>
      {parts.map((p) =>
        p.highlight ? (
          <mark key={p.key} className="note-search-highlight">
            {p.text}
          </mark>
        ) : (
          <span key={p.key}>{p.text}</span>
        )
      )}
    </Component>
  );
};

const NotesSection = ({ locals }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const rawNotes = locals?.coachEditorNotes || [];
  const { referAsClients } = locals?.activeUser || {};
  const { name: athleteName } = locals?.athlete || {};
  const { query } = useRillContext().req || { query: {} };
  const [selectedNotes, setSelectedNotes] = useState(new Set());
  /** null | { kind: 'bulk' } | { kind: 'single', noteId } */
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const notes = useMemo(() => {
    return [...rawNotes].sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.updatedAt) - new Date(a.updatedAt);
    });
  }, [rawNotes]);

  const plainBodyByNoteId = useMemo(() => {
    const m = new Map();
    for (const n of notes) {
      m.set(n._id, extractPlainTextFromDoc(n.content));
    }
    return m;
  }, [notes]);

  const filteredNotes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((n) => {
      const title = (n.title || "").toLowerCase();
      const body = (plainBodyByNoteId.get(n._id) || "").toLowerCase();
      return title.includes(q) || body.includes(q);
    });
  }, [notes, searchQuery, plainBodyByNoteId]);

  const toggleSelected = useCallback((noteId) => {
    setSelectedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(noteId)) next.delete(noteId);
      else next.add(noteId);
      return next;
    });
  }, []);

  const openCreateModal = () => {
    submit("/actions/refresh?", {
      method: "GET",
      body: {
        "modal[type]": "create-note",
      },
    });
  };

  const selectedCount = selectedNotes.size;
  const hasSelection = selectedCount > 0;

  const handleConfirmDelete = useCallback(() => {
    if (!deleteDialog) return;
    const dialog = deleteDialog;
    setDeleteDialog(null);
    const noteIds =
      dialog.kind === "bulk" ? [...selectedNotes] : [dialog.noteId];
    submit(`/actions/client-notes/delete?proxyAthlete=${query.athlete}`, {
      method: "POST",
      body: {
        noteIds: JSON.stringify(noteIds),
      },
    });
  }, [deleteDialog, query.athlete, selectedNotes]);

  if (!notes.length) {
    const displayName = athleteName?.split(" ")[0] ?? athleteName;
    return (
      <div className="notes-wrapper">
        <section className="notes-empty-state">
          <h4>{t("profile.notes.emptyTitle")}</h4>
          <p className="p2">{t("profile.notes.emptyDescription")}</p>
          <div className="chip info">
            <Trans
              t={t}
              i18nKey="profile.notes.emptyChipPrivate"
              values={{ name: displayName }}
              components={{ strong: <strong /> }}
            />
          </div>
          <button
            type="button"
            data-button-type="primary"
            onClick={openCreateModal}
          >
            <SvgIcon
              src="/img/new-icons/add.svg"
              alt=""
              noFilterOnLight={true}
            />
            {t("profile.notes.createNote")}
          </button>
        </section>
      </div>
    );
  }

  const searchTrimmed = searchQuery.trim();
  const noSearchMatches =
    searchTrimmed.length > 0 && filteredNotes.length === 0;

  return (
    <div className="notes-wrapper">
      <div className="notes-section-header">
        <div className="notes-section-search-field">
          <div className="search-container">
            <input
              type="search"
              className="w-full! input-search mt-0!"
              placeholder={t("profile.notes.searchPlaceholder")}
              aria-label={t("profile.notes.searchAriaLabel")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>
        {hasSelection ? (
          <div className="notes-section-header-actions">
            <button
              type="button"
              data-button-type="secondary"
              onClick={() => setSelectedNotes(new Set())}
            >
              <span>{t("common:cancel")}</span>
            </button>
            <button
              type="button"
              data-button-type="secondary"
              data-action="cancel"
              onClick={() => setDeleteDialog({ kind: "bulk" })}
            >
              {t("profile.notes.deleteNotesCount", { count: selectedCount })}
            </button>
          </div>
        ) : (
          <div className="notes-section-header-actions">
            <div className="chip info">
              {t("profile.notes.visibilityChip", {
                role: t(referAsClients ? "programs.client" : "programs.athlete"),
              })}
            </div>
            <button
              type="button"
              data-button-type="primary"
              onClick={openCreateModal}
            >
              <SvgIcon
                src="/img/new-icons/add.svg"
                alt=""
                noFilterOnLight={true}
              />
              {t("profile.notes.createNote")}
            </button>
          </div>
        )}
      </div>
      <ConfirmationModal
        isOpen={deleteDialog != null}
        title={t("profile.notes.deleteModalTitle")}
        contentText={
          deleteDialog?.kind === "bulk"
            ? selectedCount === 1
              ? t("profile.notes.deleteModalConfirm")
              : t("profile.notes.deleteModalConfirmBulk", {
                  count: selectedCount,
                })
            : t("profile.notes.deleteModalConfirm")
        }
        cancelButtonText={t("common:cancel")}
        cancelButtonAction={() => setDeleteDialog(null)}
        confirmButtonText={t("common:delete")}
        confirmButtonAction={handleConfirmDelete}
        reduceSize={true}
        icon="delete"
      />
      {noSearchMatches ? (
        <div className="notes-search-empty">
          <p className="p2 notes-search-empty-message">
            <Trans
              t={t}
              i18nKey="profile.notes.noMatch"
              values={{ query: searchTrimmed }}
              components={{ strong: <strong /> }}
            />
          </p>
        </div>
      ) : (
        <div className="notes-list">
          {filteredNotes.map((note) => (
            <NoteCard
              key={note._id}
              note={note}
              query={query}
              plainBody={plainBodyByNoteId.get(note._id) || ""}
              searchQuery={searchTrimmed}
              isSelected={selectedNotes.has(note._id)}
              onToggleSelect={toggleSelected}
              onRequestDelete={(noteId) =>
                setDeleteDialog({ kind: "single", noteId })
              }
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const NoteCard = ({
  note,
  query,
  plainBody,
  searchQuery,
  isSelected,
  onToggleSelect,
  onRequestDelete,
  t,
}) => {
  const { locals } = useRillContext();
  const { webAppTheme = "dark" } = locals.activeUser;
  const q = searchQuery?.trim() || "";
  const activeSearch = q.length > 0;

  let previewText = "";
  let previewHighlight = false;
  if (plainBody) {
    if (!activeSearch) {
      previewText =
        plainBody.length > PREVIEW_TRUNCATE
          ? `${plainBody.slice(0, PREVIEW_TRUNCATE)}...`
          : plainBody;
    } else {
      const snippet = buildSnippetAroundMatch(plainBody, q);
      if (snippet) {
        previewText = snippet;
        previewHighlight = true;
      } else {
        previewText =
          plainBody.length > PREVIEW_TRUNCATE
            ? `${plainBody.slice(0, PREVIEW_TRUNCATE)}...`
            : plainBody;
      }
    }
  }

  const richPreviewNodes = useMemo(() => {
    if (!plainBody) return null;
    const doc = parseDocForPreview(note.content);
    if (!doc) return null;
    return buildRichPreviewNodes(doc, PREVIEW_TRUNCATE);
  }, [note.content, plainBody]);

  const updatedDate = moment(note.updatedAt).format(
    t("profile.notes.updatedAtFormat"),
  );

  const coach = note.coachId || {};
  const coachName = coach.name || t("profile.notes.unknownCoach");
  const avatarUrl = getAvatarUrl(coach, false, webAppTheme);

  const openDetail = () => {
    submit("/actions/refresh?", {
      method: "GET",
      body: {
        "modal[type]": "view-note",
        noteId: note._id,
      },
    });
  };

  const openEdit = (e) => {
    e.stopPropagation();
    submit("/actions/refresh?", {
      method: "GET",
      body: {
        "modal[type]": "create-note",
        noteId: note._id,
      },
    });
  };

  const handlePin = (e) => {
    e.stopPropagation();
    submit(`/actions/client-notes/toggle-pin?proxyAthlete=${query.athlete}`, {
      method: "POST",
      body: {
        noteId: note._id,
        isPinned: !note.isPinned,
      },
    });
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onRequestDelete(note._id);
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: card toggles selection on click; inner controls handle keyboard
    <article
      className={`note-card${note.isPinned ? " note-card--pinned" : ""}`}
      onClick={() => {
        onToggleSelect(note._id);
      }}
    >
      <div className="note-card-row">
        <div className="note-card-left">
          <SvgIcon
            src={`/img/new-icons/${isSelected ? "checked" : "unchecked"}-square.svg`}
            noFilterOnLight={isSelected}
            onClickFn={(e) => {
              e.stopPropagation();
              onToggleSelect(note._id);
            }}
          />
          <img
            className="note-card-avatar"
            src={avatarUrl}
            alt={coachName}
            loading="lazy"
          />
          <div className="note-card-meta">
            <span className="note-card-coach-name">{coachName}</span>
            <span className="note-card-date">
              {note.isPinned && (
                <span className="note-card-pinned-label">
                  {t("profile.notes.pinned")}
                </span>
              )}
              {updatedDate}
            </span>
          </div>
        </div>
        <div className="note-card-actions">
          <button
            type="button"
            className="note-card-view-btn"
            data-button-type="outline"
            onClick={(e) => {
              e.stopPropagation();
              openDetail();
            }}
          >
            {t("profile.notes.view")}
          </button>
          <button
            type="button"
            className="note-card-action-compact"
            data-button-type="secondary"
            onClick={handlePin}
          >
            {note.isPinned ? (
              <SvgIcon
                src="/img/new-icons/push-pin-yellow.svg"
                alt={t("profile.notes.unpin")}
                noFilterOnLight={true}
              />
            ) : (
              <SvgIcon src="/img/new-icons/push-pin.svg" alt={t("profile.notes.pin")} />
            )}
          </button>
          <button
            type="button"
            className="note-card-action-compact"
            data-button-type="secondary"
            onClick={openEdit}
          >
            <SvgIcon
              src="/img/new-icons/edit-pencil.svg"
              alt={t("profile.notes.editNote")}
            />
          </button>
          <button
            type="button"
            className="note-card-action-compact"
            data-button-type="secondary"
            onClick={handleDeleteClick}
          >
            <SvgIcon
              src="/img/new-icons/red-trash.svg"
              alt={t("profile.notes.deleteNoteAction")}
            />
          </button>
        </div>
      </div>
      {note.title &&
        (activeSearch ? (
          <HighlightedText
            as="h4"
            className="note-card-title"
            text={note.title}
            searchQuery={q}
          />
        ) : (
          <h4 className="note-card-title">{note.title}</h4>
        ))}
      {previewText &&
        (previewHighlight ? (
          <div className="note-card-preview">
            <HighlightedText
              text={stripHtmlTagsForPreview(previewText)}
              searchQuery={q}
            />
          </div>
        ) : richPreviewNodes ? (
          <div className="note-card-preview">{richPreviewNodes}</div>
        ) : (
          <div className="note-card-preview">{previewText}</div>
        ))}
    </article>
  );
};

export default NotesSection;
